import type { FC } from 'react'
import { useState } from 'react'
import { useHistory } from 'react-router-dom'
import styled from 'styled-components'

import DeclaredIncome from '@/components/DeclaredIncome'
import Detail from '@/components/Detail'
import EditButton from '@/components/EditButton'
import EditRefereePanel from '@/components/EntityPanels/Referee'
import ExpandeableCard from '@/components/ExpandableCard'
import SectionBreak from '@/components/Filter/SectionBreak'
import H5 from '@/components/H5'
import TooltipIcon from '@/components/IconGroup/TooltipIcon'
import ReferenceOutcomeStatusPill from '@/components/ReferenceOutcomeStatusPill'
import SmallPill from '@/components/SmallPill'
import { ItemHeader } from '@/components/Style/Common'
import SupportingDocuments from '@/components/SupportingDocuments'
import type { AccountantReference, Application } from '@/types/Activity'
import { parseDate, toDateLabel } from '@/utils/date'
import { toLabel } from '@/utils/helpers'
import { hasCompletedBotJourney, getReferencedApplicantSubjectId } from '@/utils/referee'

const Container = styled.div`
  display: grid;
  grid-gap: 15px;
`

const AccountantDetailsContainer = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr 1fr;
  grid-gap: 15px;
`

interface Props {
  application: Application
}

const AccountantReferenceCard: FC<Props> = ({
  application: {
    customerId,
    subjectId,
    context,
    attachedSubjects,
    reference,
    milestones,
  },
}) => {
  const history = useHistory()

  const accountantReference = reference as AccountantReference
  const [editPanelOpen, seIsEditPanelOpen] = useState(false)

  const navigateToApplicant = (applicantId: string) =>
    history.push(`/applicant/${applicantId}`)

  const referencedApplicantSubjectId = getReferencedApplicantSubjectId(attachedSubjects)

  const completedMilestone = milestones.find(
    milestone => milestone.event === 'subject.complete',
  )

  const isEditDisabled =
    (attachedSubjects && attachedSubjects.length > 1) || completedMilestone !== undefined

  return (
    <>
      {editPanelOpen && (
        <EditRefereePanel
          subjectData={{
            customerId,
            subjectId,
            name: context?.name ?? '',
            email: context?.email ?? '',
            phone: context?.mobile ?? '',
          }}
          refereeType="accountant"
          setOpen={() => seIsEditPanelOpen(false)}
        />
      )}
      <ExpandeableCard
        label="Accountant Reference"
        icon="accountant"
        actionButton={
          <>
            {isEditDisabled ? (
              <TooltipIcon
                tooltipMessage={`${
                  attachedSubjects && attachedSubjects.length > 1
                    ? 'Cannot edit referee details as the referee has multiple attached applicants'
                    : 'Cannot edit referee details as the referee has completed the journey'
                }`}
                icon={
                  <EditButton
                    isDisabled={isEditDisabled}
                    onClick={() => seIsEditPanelOpen(true)}
                  />
                }
              />
            ) : (
              <EditButton onClick={() => seIsEditPanelOpen(true)} />
            )}
          </>
        }
        expandedContent={
          <Container>
            <div>
              <p>
                <strong>{context?.firstName}</strong> has been added as an accountant
                reference for{' '}
                <strong>{attachedSubjects && attachedSubjects[0].name}</strong>.
              </p>
              {referencedApplicantSubjectId !== undefined && (
                <SmallPill
                  onClick={() => navigateToApplicant(referencedApplicantSubjectId)}
                >
                  View reference
                </SmallPill>
              )}
            </div>
            <AccountantDetailsContainer>
              <Detail label="Name">
                {context?.firstName} {context?.lastName}
              </Detail>
              <Detail label="Phone">{context?.mobile}</Detail>
              <Detail label="Email">{context?.email}</Detail>
            </AccountantDetailsContainer>
            <hr />
            <div className="side-by-side-flex">
              <h2>Outcome</h2>
              <ReferenceOutcomeStatusPill
                referenceOutcomeStatus={accountantReference.outcome.status}
              />
            </div>
            {completedMilestone && (
              <div>
                <ItemHeader>References submitted</ItemHeader>
                <p>
                  {toDateLabel({
                    date: parseDate(completedMilestone.createdAt),
                    dateFormat: 'dd/MM/yyyy HH:mm:ss',
                  })}
                </p>
              </div>
            )}
            <div>
              <ItemHeader>Decision Comments</ItemHeader>
              {accountantReference?.outcome?.comments &&
                accountantReference.outcome.comments.length > 0 && (
                  <ul>
                    {accountantReference.outcome.comments.map((comment, index) => (
                      <li key={index}>{comment}</li>
                    ))}
                  </ul>
                )}
              {(!accountantReference?.outcome?.comments ||
                accountantReference?.outcome?.comments.length === 0) && (
                <p>No comments provided.</p>
              )}
              {!accountantReference.outcome && <p>Nothing here yet!</p>}
              {(!accountantReference?.outcome?.comments ||
                accountantReference?.outcome?.comments.length === 0) && (
                <p>Comments on this decision is not currently available</p>
              )}
            </div>
            {!hasCompletedBotJourney(accountantReference) ? (
              <p>Waiting for accountant to submit their information.</p>
            ) : (
              <>
                <Detail label="Position">{context?.jobRole}</Detail>
                <SectionBreak />
                <Detail label="Sources of income">
                  {accountantReference?.incomeSource}
                </Detail>
                <Detail label="Earliest date applicant began receiving the incomes">
                  {toDateLabel({
                    date: parseDate(accountantReference.startDate),
                    defaultLabel: 'Not provided',
                  })}
                </Detail>
                <SectionBreak />
                {accountantReference.taxReturnYearPeriod === 'None' ? (
                  accountantReference.incomeConfirmationType === 'None' ? (
                    <>
                      <H5>Unable to provide earnings</H5>
                      'The accountant was unable to provide any projected earnings'
                    </>
                  ) : accountantReference.incomeConfirmationType ===
                    'Projected earnings' ? (
                    <>
                      <H5>Projected earnings</H5>
                      <Detail label="Declared income">
                        <DeclaredIncome incomeProperties={accountantReference.income} />
                      </Detail>
                    </>
                  ) : accountantReference.incomeConfirmationType ===
                    'Year-to-date earnings' ? (
                    <>
                      <H5>Year to date earnings</H5>
                      <Detail label="Declared income">
                        <DeclaredIncome incomeProperties={accountantReference.income} />
                      </Detail>
                    </>
                  ) : (
                    <>An unexpected error occurred.</>
                  )
                ) : (
                  <>
                    <H5>
                      HMRC assessment tax return year:{' '}
                      {accountantReference.taxReturnYearPeriod}
                    </H5>
                    <Detail label="Declared income">
                      <DeclaredIncome incomeProperties={accountantReference.income} />
                    </Detail>
                  </>
                )}
                <SectionBreak />
                <Detail label="Additional context">
                  {toLabel(accountantReference.additionalContext, 'Not provided')}
                </Detail>
                <SectionBreak />
                <Detail label="Supporting documents">
                  {accountantReference.supportingDocuments &&
                  accountantReference.supportingDocuments.length > 0 ? (
                    <>
                      <p>
                        The accountant referee has elected to upload documents to prove
                        the reference.
                      </p>
                      <SupportingDocuments
                        supportingDocuments={accountantReference.supportingDocuments}
                        customerId={customerId}
                        subjectId={subjectId}
                      />
                    </>
                  ) : (
                    <p>
                      The accountant referee has not elected to upload documents to prove
                      the reference.
                    </p>
                  )}
                </Detail>
              </>
            )}
          </Container>
        }
        isExpanded={true}
      />
    </>
  )
}

export default AccountantReferenceCard
